import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, defer, lastValueFrom, map, repeat, takeWhile } from 'rxjs';
import { AppData } from '../helpers/app-data';
import { BaseResponse, CancelacionMasivaTareas, EstadoCancelacionMasiva } from '../interfaces/generalInterfaces';

const CLAVE_JOB_ACTIVO = 'cancelacionMasivaTareas.idJob';
const INTERVALO_CONSULTA_MS = 2000;

/** Cancelación masiva de tareas pendientes, ejecutada por el backend en segundo plano (Hangfire). */
@Injectable({
  providedIn: 'root',
})
export class TareasService {
  constructor(private http: HttpClient) {}

  /** Encola la cancelación de todas las tareas pendientes del usuario. */
  async iniciarCancelacionMasiva(observacion: string): Promise<CancelacionMasivaTareas> {
    const respuesta = await lastValueFrom(this.http.post<BaseResponse<CancelacionMasivaTareas>>(
      `${AppData.API_URL}EjecucionTarea/CancelarTareasPendientes`,
      { Observacion: observacion }
    ));
    if (!respuesta?.Ok) {
      throw new Error(this.mensaje(respuesta, 'No se pudo iniciar la cancelación de las tareas.'));
    }
    this.recordarJob(respuesta.Data.IdJob);
    return respuesta.Data;
  }

  /**
   * Consulta el estado hasta que el trabajo termine (emite también el estado final).
   * Cada consulta espera a la anterior y luego hace una pausa: nunca se solapan ni se cancelan
   * aunque el servidor tarde más que el intervalo.
   */
  seguirCancelacion(idJob: string): Observable<EstadoCancelacionMasiva> {
    return defer(() => this.http.get<BaseResponse<EstadoCancelacionMasiva>>(
      `${AppData.API_URL}EjecucionTarea/EstadoCancelacionTareas`,
      { params: { IdJob: idJob } }
    )).pipe(
      repeat({ delay: INTERVALO_CONSULTA_MS }),
      map((respuesta) => {
        if (!respuesta?.Ok) {
          throw new Error(this.mensaje(respuesta, 'No se pudo consultar el avance de la cancelación.'));
        }
        return respuesta.Data;
      }),
      takeWhile((estado) => !this.esFinal(estado), true)
    );
  }

  /** Job iniciado en esta sesión del navegador que aún no se ha mostrado como terminado. */
  jobActivo(): string | null {
    try {
      return sessionStorage.getItem(CLAVE_JOB_ACTIVO);
    } catch {
      return null;
    }
  }

  olvidarJob(): void {
    try {
      sessionStorage.removeItem(CLAVE_JOB_ACTIVO);
    } catch {
      // Sin almacenamiento disponible no hay nada que limpiar.
    }
  }

  esFinal(estado: EstadoCancelacionMasiva): boolean {
    return estado.Estado === 'Completada' || estado.Estado === 'Fallida';
  }

  private recordarJob(idJob: string): void {
    try {
      sessionStorage.setItem(CLAVE_JOB_ACTIVO, idJob);
    } catch {
      // Si el navegador bloquea el almacenamiento, el seguimiento solo dura mientras la página esté abierta.
    }
  }

  private mensaje(respuesta: BaseResponse<unknown> | null | undefined, porDefecto: string): string {
    return respuesta?.ValidationErrors?.length ? respuesta.ValidationErrors.join(' ') : (respuesta?.Error || porDefecto);
  }
}
